import { FC } from 'react'
import styled from 'styled-components'
import { Controller } from 'react-hook-form'

import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { FormAmountInput } from 'Components/Inputs/FormAmountInput'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { ControlledSelect } from 'Components/Inputs/ControlledSelect'
import { ControlledSwitch } from 'Components/Inputs/Switch/ControlledSwitch'
import { AlertBanner } from 'Components/AlertBanner/AlertBanner'

import { FlexRow, spacingLG, spacingSM } from 'Utils/styles/spacing'
import { CurrencyCode } from 'Utils/types/currencies'
import { RecurringAllowance } from 'Utils/types/allowance'
import { AppIconMap, AppIcons } from 'Utils/types/appIcons'
import { elfGreen } from 'Utils/styles/colors'

import { useRecurringAllowanceForm } from './useRecurringAllowanceForm'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${spacingLG} ${spacingSM};
  gap: ${spacingSM};
`

const FormRow = styled.div`
  display: flex;
  gap: ${spacingSM};
  align-items: end;
`

const FormButtonContainer = styled(FlexRow)`
  justify-content: flex-end;
`

const allowanceTypeOptions = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'anniversary', label: 'Anniversary' }
]

const cadenceOptions = [{ value: 'yearly', label: 'Yearly' }]

const durationOptions = [
  { value: 'end_of_month', label: 'End of Month' },
  { value: 'end_of_next_month', label: 'End of Next Month' },
  { value: 'end_of_year', label: 'End of Year' },
  { value: 'one_year', label: 'One Year' },
  { value: 'indefinite', label: 'Indefinite' }
]

type RecurringAllowanceFormProps = {
  userId: number
  benefitProgramId: number
  currencyCode: CurrencyCode
  editingRecurringAllowance?: RecurringAllowance | null
  onCancelEdit?: () => void
}

export const RecurringAllowanceForm: FC<RecurringAllowanceFormProps> = ({
  userId,
  benefitProgramId,
  currencyCode,
  editingRecurringAllowance,
  onCancelEdit
}) => {
  const isEditMode = !!editingRecurringAllowance

  const {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    registerOptions,
    currencySymbol,
    handleSubmission,
    handleError,
    isLoading,
    isValid,
    isStartDateInCurrentMonth
  } = useRecurringAllowanceForm({
    currencyCode,
    userId,
    benefitProgramId,
    editingRecurringAllowance,
    onEditComplete: onCancelEdit
  })

  return (
    <form onSubmit={handleSubmit(handleSubmission, handleError)}>
      <FormContainer>
        {isEditMode && (
          <AlertBanner
            alert={`Editing recurring allowance ID: ${editingRecurringAllowance?.id}`}
            iconSrc={AppIconMap[AppIcons.InfoCircle]}
            style={{
              padding: `0 ${spacingSM}`,
              border: `1px solid ${elfGreen}`,
              width: '50%',
              alignSelf: 'center'
            }}
          />
        )}
        <FormRow>
          <ControlledSelect
            label="Allowance Type"
            options={allowanceTypeOptions}
            defaultValue={editingRecurringAllowance?.allowanceType ?? 'birthday'}
            setControlValue={(value) => setValue('allowanceType', value, { shouldValidate: true })}
          />
          <FormAmountInput
            fieldName="amount"
            currencySymbol={currencySymbol}
            allowNegative
            labelId="manageUserRecurringAllowancesModal.amount.label"
            errorPresent={!!errors?.amount?.message}
            register={register}
            registerOptions={registerOptions.amount}
          />
        </FormRow>

        <FormRow>
          <Controller
            control={control}
            name="startDate"
            rules={registerOptions.startDate}
            render={({ field: { onChange, value } }) => (
              <FormDatePicker
                labelId="manageUserRecurringAllowancesModal.startDate.label"
                date={value}
                onChange={onChange}
                disabled={false}
                hasError={!!errors.startDate}
              />
            )}
          />
          <ControlledSelect
            label="Cadence"
            options={cadenceOptions}
            defaultValue="yearly"
            disabled={true}
            setControlValue={(value) => setValue('cadence', value)}
          />
          <ControlledSelect
            label="Duration"
            options={durationOptions}
            defaultValue={editingRecurringAllowance?.duration ?? 'end_of_year'}
            setControlValue={(value) => setValue('duration', value, { shouldValidate: true })}
          />
        </FormRow>

        {isStartDateInCurrentMonth && !isEditMode && (
          <AlertBanner
            alert="This allowance will be granted for the current month. If you don't want this, set the date to next year."
            iconSrc={AppIconMap[AppIcons.InfoCircle]}
            style={{
              padding: `0 ${spacingSM}`,
              border: `1px solid ${elfGreen}`,
              width: '80%',
              alignSelf: 'center'
            }}
          />
        )}

        <FormRow>
          <FormTextInput
            fieldName="notes"
            labelId="manageUserRecurringAllowancesModal.notes.label"
            placeholder="Optional notes"
            errorPresent={!!errors?.notes?.message}
            register={register}
            registerOptions={registerOptions.notes}
          />
          <ControlledSwitch control={control} name="active" label="Active" defaultChecked={true} />
        </FormRow>

        <FormButtonContainer>
          <StyledButton
            primary
            id={
              isEditMode
                ? 'manageUserRecurringAllowancesModal.update.label'
                : 'manageUserRecurringAllowancesModal.submit.label'
            }
            isLoading={isLoading}
            disabled={!isValid}
          />
          {isEditMode && onCancelEdit && (
            <StyledButton
              id="manageUserRecurringAllowancesModal.cancel.label"
              onClick={onCancelEdit}
              disabled={isLoading}
            />
          )}
        </FormButtonContainer>
      </FormContainer>
    </form>
  )
}
